import { Box, HStack, Icon, Input } from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"
import { FaCircleInfo } from "react-icons/fa6"
import { CheckButton, CloseButton, EditButton } from "../content/components/Button"
import { Tooltip } from "./Tooltip"
import { showErrorToast } from "../utils/toast"

export interface EditableHeadingProps {
  /** The current name. */
  value: string
  /** Called with the trimmed, changed value when the user saves. */
  onSave: (next: string) => void | Promise<void>
  /** How to render the read-only heading text (lets callers control styling/truncation). */
  renderDisplay: (value: string) => React.ReactNode
  /** Disables the controls while a save mutation is in flight. */
  saving?: boolean
  /** Disables the edit affordance entirely (e.g. not permitted). */
  disabled?: boolean
  /** Tooltip shown next to the (disabled) edit button explaining why editing is off. */
  disabledHelperText?: string
  /** Accessible label for the edit control, e.g. "asset name". */
  ariaLabel?: string
  /** Font size for the inline input (should visually match the heading). */
  inputFontSize?: string
  /** Font weight for the inline input (should visually match the heading). */
  inputFontWeight?: string
}

/**
 * An inline-editable heading: renders read-only text (via `renderDisplay`) with
 * an edit pencil immediately after it, and swaps to an inline input with
 * save/cancel controls while editing. The pencil sits after the text so it
 * stays visible even when the heading is truncated with an ellipsis.
 *
 * The inline input is rendered unstyled (no border/padding/extra height) so it
 * matches the heading's metrics exactly — switching between display and edit
 * mode does not change the row height.
 */
export const EditableHeading = ({
  value,
  onSave,
  renderDisplay,
  saving = false,
  disabled = false,
  disabledHelperText,
  ariaLabel = "name",
  inputFontSize = "24px",
  inputFontWeight = "600",
}: EditableHeadingProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  // Set while cancelling (Escape / Cancel button) so the input's blur handler
  // does not commit the discarded draft.
  const cancellingRef = useRef(false)
  // Guards against double-commit: Enter/Check triggers commit, then blur fires
  // on the same frame and would trigger a second commit (and second toast).
  const committingRef = useRef(false)

  // Keep the draft in sync with the latest value while not editing, so an
  // external update (refetch, optimistic store write) is reflected.
  useEffect(() => {
    if (!isEditing) {
      setDraft(value)
    }
  }, [value, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const startEditing = () => {
    if (disabled || saving) {
      return
    }
    setDraft(value)
    setIsEditing(true)
  }

  const cancel = () => {
    setDraft(value)
    setIsEditing(false)
  }

  const commit = async () => {
    if (cancellingRef.current) {
      cancellingRef.current = false
      return
    }
    if (committingRef.current) {
      return
    }
    committingRef.current = true
    const next = draft.trim()
    if (!next || next === value) {
      cancel()
      committingRef.current = false
      return
    }
    try {
      await onSave(next)
      setIsEditing(false)
    } catch (err: any) {
      const message =
        err?.graphQLErrors?.[0]?.message ||
        err?.message ||
        "Rename failed"
      showErrorToast("Rename failed", message)
      cancel()
    } finally {
      committingRef.current = false
    }
  }

  if (isEditing) {
    return (
      <HStack gap={2} align="center" minWidth={0} width="100%">
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              void commit()
            } else if (e.key === "Escape") {
              e.preventDefault()
              cancellingRef.current = true
              cancel()
            }
          }}
          onBlur={() => void commit()}
          disabled={saving}
          aria-label={ariaLabel}
          // Render inline with the heading's exact metrics so the row height
          // does not change when toggling edit mode.
          variant="flushed"
          unstyled
          height="auto"
          padding={0}
          border="none"
          borderRadius={0}
          fontSize={inputFontSize}
          fontWeight={inputFontWeight}
          lineHeight="inherit"
          maxWidth="520px"
          width="100%"
          _focus={{ outline: "none", boxShadow: "none" }}
          _focusVisible={{ outline: "none", boxShadow: "none" }}
        />
        <CloseButton
          size="2xs"
          onMouseDown={(e: React.MouseEvent) => {
            // Suppress the input's blur-commit so cancel discards the draft.
            e.preventDefault()
            cancellingRef.current = true
          }}
          onClick={cancel}
          aria-label={`cancel editing ${ariaLabel}`}
          disabled={saving}
        />
        <CheckButton
          size="2xs"
          onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
          onClick={() => void commit()}
          aria-label={`save ${ariaLabel}`}
          disabled={saving}
        />
      </HStack>
    )
  }

  const canEdit = !disabled && !saving

  return (
    <HStack gap={2} align="center" minWidth={0}>
      <Box
        onClick={startEditing}
        cursor={canEdit ? "text" : "default"}
        minWidth={0}
        display="flex"
        alignItems="center"
      >
        {renderDisplay(value)}
      </Box>
      <Box flexShrink={0}>
        <EditButton
          size="2xs"
          onClick={startEditing}
          aria-label={`edit ${ariaLabel}`}
          disabled={disabled || saving}
        />
      </Box>
      {disabled && disabledHelperText && (
        <Tooltip content={disabledHelperText}>
          <Icon as={FaCircleInfo} boxSize={4} color="primary" flexShrink={0} />
        </Tooltip>
      )}
    </HStack>
  )
}

export default EditableHeading
